/**
 * The prompts behind the one-click buttons — Play and the audits — as the organization sends them.
 * Each can be read, reworded for the whole organization, or put back to the catalog's; the whole
 * set can also be edited as JSON. The desktop app keeps a copy for when it is offline.
 */

import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Braces, RotateCcw, Save, Sparkles } from 'lucide-react'
import { api, ApiError } from '../lib/api'
import { useI18n } from '../lib/i18n'
import { atLeast, getSession } from '../lib/auth'
import { isDesktop } from '../lib/desktop'
import { localized, usePromptCatalog } from '../lib/prompts'
import { Badge, ErrorBanner, Skeleton } from '../components/primitives'
import { Modal } from '../components/Modal'
import { PromptAnalyzer } from '../components/PromptAnalyzer'
import { PromptReviewDialog } from '../components/PromptReviewDialog'
import type { CatalogPrompt } from '../lib/types'

export default function Prompts() {
  const { t, locale } = useI18n()
  const canEdit = atLeast(getSession()?.role, 'Developer')
  const { data: catalog, isLoading, dataUpdatedAt } = usePromptCatalog()
  const [json, setJson] = useState(false)
  const [tab, setTab] = useState<'prompts' | 'analyzer'>(() => (window.location.hash === '#analyzer' ? 'analyzer' : 'prompts'))

  // Arriving from an Audit card's "see the prompt" scrolls to it.
  useEffect(() => {
    if (!catalog || !window.location.hash) return
    document.getElementById(`prompt-${window.location.hash.slice(1)}`)?.scrollIntoView({ block: 'start' })
  }, [catalog])

  const tabs = (
    <div className="segmented" role="tablist">
      {(['prompts', 'analyzer'] as const).map((key) => (
        <button key={key} type="button" role="tab" aria-selected={tab === key} className={`segmented__item${tab === key ? ' segmented__item--active' : ''}`} onClick={() => setTab(key)}>
          {t(`prompts.tab.${key}`)}
        </button>
      ))}
    </div>
  )

  if (tab === 'analyzer') {
    return (
      <div className="stack">
        {tabs}
        <PromptAnalyzer />
      </div>
    )
  }

  return (
    <div className="stack">
      {tabs}
      <div className="row row--between row--wrap">
        <p className="muted small" style={{ maxWidth: 760, margin: 0 }}>{t('prompts.intro')}</p>
        {canEdit && (
          <button type="button" className="btn btn--sm" onClick={() => setJson(true)} disabled={!catalog}>
            <Braces size={14} /> {t('prompts.editJson')}
          </button>
        )}
      </div>
      {isDesktop() && dataUpdatedAt > 0 && (
        <span className="tiny muted">{t('prompts.cached', { at: new Date(dataUpdatedAt).toLocaleString(locale) })}</span>
      )}
      {isLoading && <Skeleton height={200} />}
      {(catalog?.prompts ?? []).map((prompt) => (
        <PromptCard key={prompt.key} prompt={prompt} canEdit={canEdit} />
      ))}
      {json && catalog && <JsonEditor prompts={catalog.prompts} onClose={() => setJson(false)} />}
    </div>
  )
}

function PromptCard({ prompt, canEdit }: { prompt: CatalogPrompt; canEdit: boolean }) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const [body, setBody] = useState(prompt.body)
  const [problem, setProblem] = useState<string | null>(null)
  const [reviewing, setReviewing] = useState(false)
  useEffect(() => setBody(prompt.body), [prompt.body])

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['prompts'] })
  const save = useMutation({
    mutationFn: () => api.prompts.save(prompt.key, body),
    onSuccess: () => {
      setProblem(null)
      refresh()
    },
    onError: (error) => setProblem(error instanceof ApiError && error.code === 'too_long' ? t('prompts.tooLong') : t('error.generic')),
  })
  const reset = useMutation({ mutationFn: () => api.prompts.reset(prompt.key), onSuccess: refresh })

  return (
    <section id={`prompt-${prompt.key}`} className="panel stack prompt-card">
      <div className="row row--tight row--wrap">
        <strong>{localized(prompt.title, locale)}</strong>
        <Badge tone="neutral">{prompt.category === 'run' ? t('prompts.category.run') : t('prompts.category.audit')}</Badge>
        <Badge tone="neutral">{t(`work.kind.${prompt.kind}`)}</Badge>
        <Badge tone={prompt.tier === 'premium' ? 'purple' : 'neutral'}>{t(`prompts.tier.${prompt.tier}`)}</Badge>
        {prompt.overridden && <Badge tone="accent">{t('prompts.custom')}</Badge>}
        {prompt.outdated && <Badge tone="warn">{t('prompts.outdated')}</Badge>}
        <span className="tiny muted mono">{prompt.key} · v{prompt.version}</span>
      </div>
      <span className="small muted">{localized(prompt.description, locale)}</span>
      {(prompt.releases?.length ?? 0) > 1 && <VersionPicker prompt={prompt} canEdit={canEdit} />}
      {prompt.newerVersion && (
        <div className="notice small prompt-card__newer">
          <strong>{t('prompts.newer', { name: localized(prompt.title, locale), version: prompt.newerVersion })}</strong>
          <ul>{(prompt.newerNotes ?? []).map((note) => <li key={note}>{note}</li>)}</ul>
          <a href="/settings/billing">{t('plans.upgrade')}</a>
        </div>
      )}
      <textarea
        className="input mono prompt-card__body"
        rows={10}
        value={body}
        readOnly={!canEdit}
        onChange={(event) => setBody(event.target.value)}
        aria-label={localized(prompt.title, locale)}
      />
      {problem && <ErrorBanner message={problem} />}
      {reviewing && (
        <PromptReviewDialog
          prompt={body}
          onClose={() => setReviewing(false)}
          onApplyOriginal={(original) => { setBody(original); setReviewing(false) }}
          onApplySuggestion={(improved) => { setBody(improved); setReviewing(false) }}
        />
      )}
      {canEdit && (
        <div className="row row--tight">
          <button type="button" className="btn btn--sm btn--primary" onClick={() => save.mutate()} disabled={save.isPending || body.trim() === prompt.body.trim()}>
            <Save size={13} /> {t('common.save')}
          </button>
          {prompt.overridden && (
            <button type="button" className="btn btn--sm btn--ghost" onClick={() => reset.mutate()} disabled={reset.isPending}>
              <RotateCcw size={13} /> {t('prompts.reset')}
            </button>
          )}
          <button type="button" className="btn btn--sm btn--ghost" onClick={() => setReviewing(true)}>
            <Sparkles size={13} /> {t('prompts.review')}
          </button>
          {prompt.updatedBy && <span className="tiny muted">{t('prompts.by', { who: prompt.updatedBy })}</span>}
        </div>
      )}
    </section>
  )
}

/**
 * Which release of the prompt the organization sends (owner, 12/10/2026): "the newest" by default, or an
 * older one someone prefers. A premium release outside the plan is listed but cannot be picked. The
 * organization's own wording, when it has one, still wins — the card says so.
 */
function VersionPicker({ prompt, canEdit }: { prompt: CatalogPrompt; canEdit: boolean }) {
  const { t, locale } = useI18n()
  const queryClient = useQueryClient()
  const releases = prompt.releases ?? []
  const choose = useMutation({
    mutationFn: (version: string | null) => api.prompts.chooseVersion(prompt.key, version),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['prompts'] }),
  })
  const shown = releases.find((r) => r.version === prompt.version)
  const label = (version: string) => (version.endsWith('.0') ? `V${version.slice(0, -2)}` : `v${version}`)

  return (
    <div className="stack prompt-card__versions">
      <label className="row row--tight row--wrap small">
        <span>{t('prompts.version.label')}</span>
        <select
          className="input input--sm"
          value={prompt.chosenVersion ?? ''}
          disabled={!canEdit || choose.isPending}
          onChange={(event) => choose.mutate(event.target.value || null)}
        >
          <option value="">{t('prompts.version.newest', { version: label(releases.filter((r) => r.available).at(-1)?.version ?? prompt.version) })}</option>
          {[...releases].reverse().map((r) => (
            <option key={r.version} value={r.version} disabled={!r.available}>
              {label(r.version)}{r.releasedAt ? ` · ${r.releasedAt}` : ''}{r.available ? '' : ` · ${t('prompts.tier.premium')}`}
            </option>
          ))}
        </select>
        {prompt.overridden && <span className="tiny muted">{t('prompts.version.ownWins')}</span>}
      </label>
      {shown && shown.notes.length > 0 && (
        <ul className="tiny muted prompt-card__notes">
          {(shown.localizedNotes?.[locale] ?? shown.notes).map((note) => <li key={note}>{note}</li>)}
        </ul>
      )}
      {choose.isError && <ErrorBanner message={t('error.generic')} />}
    </div>
  )
}

/** The whole set as { key: prompt } JSON; applying it saves every prompt whose text changed. */
function JsonEditor({ prompts, onClose }: { prompts: CatalogPrompt[]; onClose: () => void }) {
  const { t } = useI18n()
  const queryClient = useQueryClient()
  const [text, setText] = useState(() => JSON.stringify(Object.fromEntries(prompts.map((p) => [p.key, p.body])), null, 2))
  const [problem, setProblem] = useState<string | null>(null)

  const apply = useMutation({
    mutationFn: async () => {
      let parsed: unknown
      try {
        parsed = JSON.parse(text)
      } catch {
        throw new Error('json')
      }
      if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) throw new Error('json')
      const entries = Object.entries(parsed as Record<string, unknown>)
      for (const [key, value] of entries) {
        const prompt = prompts.find((p) => p.key === key)
        if (!prompt || typeof value !== 'string') throw new Error('json')
        if (value.trim() !== prompt.body.trim()) await api.prompts.save(key, value)
      }
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['prompts'] })
      onClose()
    },
    onError: (error) => setProblem(error instanceof Error && error.message === 'json' ? t('prompts.badJson') : t('error.generic')),
  })

  return (
    <Modal title={t('prompts.editJson')} onClose={onClose} wide>
      <div className="stack">
        <span className="tiny muted">{t('prompts.jsonHint')}</span>
        <textarea className="input mono prompt-card__body" rows={22} value={text} onChange={(event) => setText(event.target.value)} spellCheck={false} aria-label="JSON" />
        {problem && <ErrorBanner message={problem} />}
        <div className="row row--end">
          <button type="button" className="btn btn--ghost" onClick={onClose}>{t('common.cancel')}</button>
          <button type="button" className="btn btn--primary" onClick={() => apply.mutate()} disabled={apply.isPending}>
            {t('prompts.applyJson')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
